/**
 * ShareModal Component
 * Modal for sharing memories via link or social media image
 */

import React, { useState, useEffect, useCallback } from 'react';
import './ShareModal.css';
import SendNoteModal from '../SendNoteModal';
import { generatePreview, generateScreenshot, SCREENSHOT_FORMATS } from '../../utils/screenshotGenerator';
import { getShareableUrl, copyToClipboard, shareMemory, downloadBlob, canUseNativeShare } from '../../utils/shareUtils';
import { logger } from '../../utils/logger';

const FORMAT_OPTIONS = [
  { key: 'PORTRAIT', icon: '📱', name: 'Stories' },
  { key: 'SQUARE', icon: '🖼️', name: 'Square' },
  { key: 'LANDSCAPE', icon: '🖥️', name: 'Card' },
];

export default function ShareModal({ memory, theme, user, onClose }) {
  const [showSendNote, setShowSendNote] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('PORTRAIT');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const shareUrl = getShareableUrl(memory.id);

  const loadPreview = useCallback(async () => {
    if (!memory) return;

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const dataUrl = await generatePreview({
        memory,
        format: selectedFormat,
        theme,
      });
      setPreviewUrl(dataUrl);
    } catch (err) {
      logger.error('Failed to generate preview:', err);
      setPreviewError('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [memory, selectedFormat, theme]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // Copy link to clipboard
  const handleCopyLink = async () => {
    const success = await copyToClipboard(shareUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Share via native share or clipboard
  const handleShare = async () => {
    setSharing(true);
    setFeedback(null);

    try {
      // Generate full-size image for sharing
      const imageBlob = await generateScreenshot({
        memory,
        format: selectedFormat,
        theme,
      });

      const result = await shareMemory({
        url: shareUrl,
        title: 'A memory from Velvynote',
        text: memory.text?.substring(0, 100) || 'Check out this memory!',
        imageBlob,
      });

      if (result.success) {
        if (result.method === 'clipboard') {
          setFeedback({ type: 'success', message: 'Link copied to clipboard!' });
        } else if (result.method === 'native' || result.method === 'native-with-image') {
          setFeedback({ type: 'success', message: 'Shared successfully!' });
        }
      } else if (result.method === 'cancelled') {
        // User cancelled, no feedback needed
      } else {
        setFeedback({ type: 'error', message: 'Could not share. Link copied instead.' });
      }
    } catch (err) {
      logger.error('Share failed:', err);
      setFeedback({ type: 'error', message: 'Failed to share. Please try copying the link.' });
    } finally {
      setSharing(false);
    }
  };

  // Download image
  const handleDownload = async () => {
    setDownloading(true);
    setFeedback(null);

    try {
      const imageBlob = await generateScreenshot({
        memory,
        format: selectedFormat,
        theme,
      });

      const formatConfig = SCREENSHOT_FORMATS[selectedFormat];
      const filename = `velvynote-memory-${formatConfig.name.toLowerCase()}.png`;
      downloadBlob(imageBlob, filename);

      setFeedback({ type: 'success', message: 'Image downloaded!' });
    } catch (err) {
      logger.error('Download failed:', err);
      setFeedback({ type: 'error', message: 'Failed to download image.' });
    } finally {
      setDownloading(false);
    }
  };

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <>
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>
            <span>📤</span>
            Share Memory
          </h2>
          <button
            className="share-close-button"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="share-modal-content">
          {/* Send privately — the premium, person-to-person path */}
          {user && (
            <button
              className="share-send-private"
              onClick={() => setShowSendNote(true)}
            >
              <span className="share-send-private-mark">✦</span>
              <span className="share-send-private-text">
                <strong>Send privately to someone</strong>
                <small>Leave this note for one person, just for them</small>
              </span>
              <span className="share-send-private-arrow">→</span>
            </button>
          )}

          {/* Preview Section */}
          <div className="share-preview-section">
            <div className="share-preview-container">
              {previewLoading && (
                <div className="share-preview-loading">
                  <div className="share-preview-spinner" />
                  <span>Generating preview...</span>
                </div>
              )}
              {previewError && !previewLoading && (
                <div className="share-preview-error">{previewError}</div>
              )}
              {previewUrl && !previewLoading && (
                <img
                  src={previewUrl}
                  alt="Memory preview"
                  className="share-preview-image"
                />
              )}
            </div>
          </div>

          {/* Format Selector */}
          <div className="share-format-section">
            <span className="share-format-label">Image format</span>
            <div className="share-format-options">
              {FORMAT_OPTIONS.map((format) => (
                <button
                  key={format.key}
                  className={`share-format-option ${selectedFormat === format.key ? 'selected' : ''}`}
                  onClick={() => setSelectedFormat(format.key)}
                >
                  <div className="share-format-icon">{format.icon}</div>
                  <div className="share-format-name">{format.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Link Section */}
          <div className="share-link-section">
            <span className="share-link-label">Share link</span>
            <div className="share-link-container">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="share-link-input"
                onFocus={(e) => e.target.select()}
              />
              <button
                className={`share-copy-button ${copied ? 'copied' : ''}`}
                onClick={handleCopyLink}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="share-actions">
            <button
              className="share-action-button primary"
              onClick={handleShare}
              disabled={sharing || previewLoading}
            >
              {sharing ? (
                <>
                  <span className="share-preview-spinner" style={{ width: 18, height: 18 }} />
                  Sharing...
                </>
              ) : (
                <>
                  {canUseNativeShare() ? '📤 Share' : '📋 Copy Link'}
                </>
              )}
            </button>

            <button
              className="share-action-button secondary"
              onClick={handleDownload}
              disabled={downloading || previewLoading}
            >
              {downloading ? (
                <>
                  <span className="share-preview-spinner" style={{ width: 18, height: 18 }} />
                  Downloading...
                </>
              ) : (
                <>⬇️ Download Image</>
              )}
            </button>
          </div>

          {/* Feedback */}
          {feedback && (
            <div className={`share-feedback ${feedback.type}`}>
              {feedback.message}
            </div>
          )}
        </div>
      </div>
    </div>

      {showSendNote && (
        <SendNoteModal
          memory={memory}
          user={user}
          onClose={() => setShowSendNote(false)}
        />
      )}
    </>
  );
}
